// Shared layout for the auth pages (Login, TOTP challenge, TOTP enroll,
// forgot password, forced password change), and /admin's two notices.
//
// The template's second login design (next-shadcn-admin-dashboard:
// src/app/(main)/auth/v2): the page's form on the left, on the plain page with
// no card, and the brand on the right in a rounded panel of the main colour,
// with a short write-up and a live-looking product card. Below 1024px the
// panel is hidden and only the form shows. Pages still own their form markup;
// this is the chrome.
//
// Styles live in ../auth.css and read theme.css's tokens only (pass 007), so
// the frame follows the theme of whatever it renders in. Records:
// docs/testing/ui-migration/007-auth-css.md, 032-auth-shell.md.

import { useEffect, useRef, useState, type ReactNode } from "react";
// The sign-in pages render outside the app Shell, so they load the theme and
// the shared control spec themselves — the same inputs and buttons as the app.
import "../theme.css";
import "../shell-helix-data.css";
import "../hx-controls.css";
import "../auth.css";

function Tick() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

function Mark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 5.5C4 4.7 4.7 4 5.5 4h13c.8 0 1.5.7 1.5 1.5v9c0 .8-.7 1.5-1.5 1.5H9l-4 4v-4H5.5C4.7 16 4 15.3 4 14.5v-9z" />
      <path d="M8.5 9.5h7M8.5 12.5h4" />
    </svg>
  );
}


// ── The product card: a conversation that is actually happening ──────────────
// A scripted thread plays on a loop: whoever speaks next "types" for a beat,
// the message slides in, the card scrolls to keep up, a teammate joins midway,
// and after the last line it rests, clears and starts again. Under a
// reduced-motion preference the whole thread is shown at once, still.
type DemoTurn =
  | { who: "cust" | "ai" | "human"; text: string; typing: number }
  | { who: "join"; text: string; typing: number };

const DEMO_SCRIPT: DemoTurn[] = [
  { who: "cust", text: "Hi! We run a 12-person support team. Could Chloe take our first-line chat and WhatsApp?", typing: 1300 },
  { who: "ai", text: "Absolutely. I answer from your own help centre, handle order and booking questions end to end, and pass anything sensitive to your team with the full context attached.", typing: 1700 },
  { who: "ai", text: "So I point you to the right plan: roughly how many conversations a month?", typing: 1200 },
  { who: "cust", text: "Around 4,000. And we'd want to be live before the holiday rush.", typing: 1200 },
  { who: "join", text: "Aashi joined the conversation", typing: 900 },
  { who: "human", text: "Hi, Aashi from GoZupees. 4,000 a month fits our Growth plan, and a two-week go-live is realistic. Shall I hold a 20-minute slot tomorrow to walk you through it?", typing: 2100 },
  { who: "cust", text: "Yes please, tomorrow afternoon works.", typing: 900 },
];

/** Reveals `text` word by word, the way an agent's reply streams in. */
const STREAM_MS_PER_WORD = 55;
function Streamed({ text, stream }: { text: string; stream: boolean }) {
  const words = text.split(" ");
  const [n, setN] = useState(stream ? 0 : words.length);
  useEffect(() => {
    if (!stream) return;
    let i = 0;
    const t = window.setInterval(() => {
      i++;
      setN(i);
      if (i >= words.length) window.clearInterval(t);
    }, STREAM_MS_PER_WORD);
    return () => window.clearInterval(t);
  }, [text, stream, words.length]);
  const done = n >= words.length;
  return (
    <>
      {words.slice(0, n).join(" ")}
      {!done && <span className="caret" />}
    </>
  );
}

function LiveDemo() {
  const [reduced] = useState(() => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  // `shown` turns are on screen; `typing` is who is about to speak, if anyone.
  const [shown, setShown] = useState<DemoTurn[]>(() => (reduced ? DEMO_SCRIPT : []));
  const [typing, setTyping] = useState<DemoTurn["who"] | null>(null);
  const [fading, setFading] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (reduced) return;
    let cancelled = false;
    let timer = 0;
    const wait = (ms: number) => new Promise<void>((r) => { timer = window.setTimeout(r, ms); });
    (async () => {
      while (!cancelled) {
        setFading(false);
        setShown([]);
        setTyping(null);
        await wait(700);
        for (const turn of DEMO_SCRIPT) {
          if (cancelled) return;
          setTyping(turn.who);
          await wait(turn.typing);
          if (cancelled) return;
          setTyping(null);
          setShown((cur) => [...cur, turn]);
          const streamMs = turn.who === "cust" || turn.who === "join" ? 0 : turn.text.split(" ").length * STREAM_MS_PER_WORD;
          await wait(turn.who === "join" ? 700 : streamMs + 1100 + Math.min(1200, turn.text.length * 6));
        }
        await wait(3200);
        if (cancelled) return;
        setFading(true);
        await wait(600);
      }
    })();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [reduced]);

  // Keep the newest line in view, like a real chat window.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: reduced ? "auto" : "smooth" });
  }, [shown, typing, reduced]);

  const humanOn = shown.some((t) => t.who === "join");
  return (
    <div className={"hxa-demo" + (fading ? " fade" : "")} aria-hidden="true">
      <div className="hd">
        <span className="av">AI</span>
        <div>
          <b>Chloe</b>
          <small>{humanOn ? "● Aashi is on the thread · GoZupees" : "● Online · GoZupees"}</small>
        </div>
        <span className="dots">•••</span>
      </div>
      <div className="bd" ref={bodyRef}>
        {shown.map((t, i) =>
          t.who === "join" ? (
            <div key={i} className="join"><i /> {t.text}</div>
          ) : (
            <div key={i} className={"m " + t.who}>
              {t.who !== "cust" && <span className={"a" + (t.who === "human" ? " hu" : "")}>{t.who === "human" ? "AA" : ""}</span>}
              <div>
                {t.who === "human" && <div className="lbl">Aashi · Human</div>}
                <div className="b"><Streamed text={t.text} stream={!reduced && t.who !== "cust"} /></div>
              </div>
            </div>
          ),
        )}
        {typing && typing !== "join" && (
          <div className={"m " + typing}>
            {typing !== "cust" && <span className={"a" + (typing === "human" ? " hu" : "")}>{typing === "human" ? "AA" : ""}</span>}
            <div className="b typing"><i /><i /><i /></div>
          </div>
        )}
        {typing === "join" && <div className="join"><i /> Bringing in the team…</div>}
      </div>
    </div>
  );
}

export default function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="hxa">
      <section className="hxa-brand" aria-label="About Helix CX">
        <a className="hxa-logo" href="/ops/">
          <span className="mark"><Mark /></span>
          <span className="word">Helix<span>CX AI</span></span>
        </a>

        <div className="hxa-pill"><span className="dot" />Live across chat, WhatsApp, email and voice</div>

        <h1 className="hxa-h1">
          Every conversation,<br />
          <span className="grad">handled.</span>
        </h1>
        <p className="hxa-lede">
          Helix runs your AI agents on every channel, answers from your own
          knowledge, and hands the conversations that need a person to your
          team with the full story attached. Sign in to your workspace to see
          what it handled today.
        </p>

        <ul className="hxa-ticks">
          <li><Tick /> Human handoff built in</li>
          <li><Tick /> One timeline per customer</li>
          <li><Tick /> Every workspace isolated</li>
        </ul>

        <LiveDemo />
      </section>

      <div className="hxa-side">
        <div className="hxa-card">
          <h1>{title}</h1>
          {subtitle && <p className="sub">{subtitle}</p>}
          <div className="body">{children}</div>
          {footer && <div className="foot">{footer}</div>}
        </div>
        <p className="hxa-fine">
          Access is for workspace members only. Two-factor authentication is
          required on every account.
        </p>
      </div>
    </div>
  );
}
